import { z } from 'zod';

export const FORWARDING_KEY = 'partner_forwarding_v1';
export const forwardingSchema = z
  .object({
    revision: z.number().int().nonnegative(),
    enabled: z.boolean(),
    websiteEnabled: z.boolean(),
    websitePartnerId: z.string().uuid().nullable().optional(),
    managedWhatsappEnabled: z.boolean().default(false),
    partnerIds: z.array(z.string().uuid()).max(1000),
  })
  .strict();
export const forwardingDefaults = {
  revision: 0,
  enabled: false,
  websiteEnabled: false,
  websitePartnerId: null as string | null,
  managedWhatsappEnabled: false,
  partnerIds: [] as string[],
};
export function parseForwarding(value?: string | null) {
  return value == null
    ? { ...forwardingDefaults, partnerIds: [] }
    : forwardingSchema.parse(JSON.parse(value));
}
export function allowsDirect(
  config: ReturnType<typeof parseForwarding>,
  partnerId?: string | null
) {
  return (
    config.enabled && (partnerId ? config.partnerIds.includes(partnerId) : config.websiteEnabled)
  );
}
export const directDetailsSchema = z
  .object({
    requestId: z.string().uuid(),
    name: z
      .string()
      .trim()
      .min(2)
      .max(100)
      .refine((value) => !/[<>\x00-\x1f]/.test(value)),
    mobile: z.string().regex(/^[6-9]\d{9}$/),
    loanType: z.string().trim().min(1).max(100),
    productCode: z.string().regex(/^[A-Za-z0-9_-]{1,80}$/).optional(),
    mode: z.enum(['direct','portal']).optional(),
    consent: z.literal(true),
    whatsappConsent: z.boolean(),
    partnerCode: z
      .string()
      .regex(/^[A-Za-z0-9_-]{1,64}$/)
      .optional(),
  })
  .strict();
export const DIRECT_CONSENT =
  'I consent to RUPIQA recording my name, mobile and loan requirement and sharing this application reference with Poonawalla Fincorp. Lender approval is not guaranteed.';
export const DIRECT_WHATSAPP_CONSENT =
  'I agree to receive my application link on WhatsApp from RUPIQA or my referring partner.';

export function directApplicationLink(code: string) {
  if (!/^PV1[A-F0-9]{32}$/.test(code)) throw new Error('Invalid saved application link');
  const bytes = code
    .slice(3)
    .match(/../g)!
    .map((hex) => String.fromCharCode(parseInt(hex, 16)))
    .join('');
  const token = btoa(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  return `https://www.rupiqa.in/r/${token}`;
}

export function portalApplicationLink(token: string) {
  if (!/^[A-Za-z0-9_-]{22}$/.test(token)) throw new Error('Invalid saved portal link');
  return `https://www.rupiqa.in/r/${token}`;
}

// Shared response boundary for both saved journeys. Only canonical branded
// continuation links are accepted; never trust arbitrary URLs from a response.
export function validSavedApplicationLink(link: unknown, applicationNo: unknown, mode: unknown): boolean {
  if (typeof applicationNo !== 'string' || !/^RQ[A-F0-9]{12}$/.test(applicationNo) || typeof link !== 'string') return false;
  if (mode !== 'direct' && mode !== 'portal') return false;
  if (/^https:\/\/www\.rupiqa\.in\/r\/[A-Za-z0-9_-]{22}$/.test(link)) return true;
  return mode === 'portal'
    ? link === `https://www.rupiqa.in/rupiqa/customer?application=${applicationNo}`
    : /^https:\/\/www\.rupiqa\.in\/r\/utm\/PV1[A-F0-9]{32}$/.test(link);
}

// Lossless 128-bit alias: no truncation, new mapping table or change to existing links.
export function expandApplicationToken(token: string) {
  if (!/^[A-Za-z0-9_-]{22}$/.test(token)) return null;
  const bytes = atob(token.replace(/-/g, '+').replace(/_/g, '/') + '==');
  const code =
    'PV1' +
    Array.from(bytes, (char) => char.charCodeAt(0).toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
  return directApplicationLink(code).endsWith('/' + token) ? code : null;
}

export function applicationWhatsappMessage(name: string, link: string, lenderName = 'Poonawalla Fincorp') {
  return `Hi ${name}, continue your application with ${lenderName}:\n${link}\nApproval is subject to lender policy.`;
}
